"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { CreateDropConfig } from "@/entities/IDrop";
import CreateDropStormParts from "./CreateDropStormParts";
import { AnimatePresence, motion } from "framer-motion";
import CreateDropContent from "./CreateDropContent";
import CreateCurationDropContent from "./CreateCurationDropContent";
import QuorumProposalDropModal from "./quorum/QuorumProposalDropModal";
import { useMutation } from "@tanstack/react-query";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ReactQueryWrapperContext } from "../react-query-wrapper/ReactQueryWrapper";
import { commonApiPost } from "@/services/api/common-api";
import type { ApiCreateDropRequest } from "@/generated/models/ApiCreateDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { AuthContext } from "../auth/Auth";
import { useKeyPressEvent } from "react-use";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { CurationComposerVariant } from "./dropComposer.types";
import { DropMode } from "./dropComposer.types";
import type { DropPrivileges } from "@/hooks/useDropPriviledges";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useUnreadDividerOptional } from "@/contexts/wave/UnreadDividerContext";
import { useWave } from "@/hooks/useWave";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { getMentionedGroupsFromParts } from "@/helpers/waves/drop-group-mentions";
interface CreateDropProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly onExitFixedDropMode?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly privileges: DropPrivileges;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
  readonly externalAttachmentDrop?: {
    readonly token: number;
    readonly files: File[];
  } | null | undefined;
  readonly onExternalAttachmentDropConsumed?: (() => void) | undefined;
}
export interface DropMutationBody {
  readonly drop: ApiCreateDropRequest;
  readonly dropId: string | null;
  readonly onSuccess?: (() => void) | undefined;
  readonly onError?: ((error: unknown) => void) | undefined;
}
const ANIMATION_DURATION = 0.3;
export default function CreateDrop(t0) {
  const $ = _c(115);
  const {
    activeDrop,
    onCancelReplyQuote,
    onDropAddedToQueue,
    onAllDropsAdded,
    onExitFixedDropMode,
    wave,
    dropId,
    fixedDropMode,
    privileges,
    curationComposerVariant: t1,
    externalAttachmentDrop,
    onExternalAttachmentDropConsumed
  } = t0;
  const curationComposerVariant = t1 === undefined ? "default" : t1;
  const {
    setToast
  } = useContext(AuthContext);
  const {
    waitAndInvalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const unreadDividerContext = useUnreadDividerOptional();
  let t2;
  if ($[0] !== onCancelReplyQuote) {
    t2 = () => onCancelReplyQuote();
    $[0] = onCancelReplyQuote;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useKeyPressEvent("Escape", t2);
  const [isStormMode, setIsStormMode] = useState(false);
  const [drop, setDrop] = useState(null);
  const [dropModeOverride, setDropModeOverride] = useState(null);
  const [curationPrefillSeed, setCurationPrefillSeed] = useState(null);
  const [dismissedQuorumProposalScope, setDismissedQuorumProposalScope] = useState(null);
  const {
    processDropRemoved,
    processIncomingDrop
  } = useMyStream();
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave
  } = useWave(wave);
  const t3 = wave.participation.submission_strategy ?? null;
  let t4;
  if ($[2] !== isCurationWave || $[3] !== isMemesWave || $[4] !== isQuorumWave || $[5] !== t3) {
    t4 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t3
    });
    $[2] = isCurationWave;
    $[3] = isMemesWave;
    $[4] = isQuorumWave;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const submissionExperience = t4;
  let t5;
  if ($[7] !== activeDrop || $[8] !== fixedDropMode || $[9] !== wave.chat.authenticated_user_eligible || $[10] !== wave.participation.authenticated_user_eligible) {
    t5 = () => {
      if (fixedDropMode === DropMode.CHAT) {
        return false;
      }
      if (fixedDropMode === DropMode.PARTICIPATION) {
        return true;
      }
      if (wave.chat.authenticated_user_eligible) {
        return false;
      }
      if (wave.participation.authenticated_user_eligible) {
        return true;
      }
      if (activeDrop) {
        return false;
      }
      return false;
    };
    $[7] = activeDrop;
    $[8] = fixedDropMode;
    $[9] = wave.chat.authenticated_user_eligible;
    $[10] = wave.participation.authenticated_user_eligible;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const getDefaultIsDropMode = t5;
  const activeDropScope = activeDrop === null ? "none" : `${activeDrop.action}:${activeDrop.drop.id}:${activeDrop.partId}`;
  const modeScopeKey = `${wave.id}:${fixedDropMode}:${wave.chat.authenticated_user_eligible}:${wave.participation.authenticated_user_eligible}:${activeDropScope}`;
  const modeScopeToken = modeScopeKey;
  const defaultIsDropMode = getDefaultIsDropMode();
  const isDropMode = dropModeOverride?.scopeKey === modeScopeToken ? dropModeOverride.value : defaultIsDropMode;
  const initialCurationUrl = curationPrefillSeed?.scopeKey === modeScopeToken ? curationPrefillSeed.url : null;
  const isCurationDropMode = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isDropMode;
  const isQuorumProposalDropMode = submissionExperience === WaveSubmissionExperience.QUORUM_PROPOSAL && isDropMode;
  const quorumProposalScopeKey = `${modeScopeToken}:${submissionExperience}`;
  const isQuorumProposalModalOpen = isQuorumProposalDropMode && dismissedQuorumProposalScope !== quorumProposalScopeKey;
  const canMentionAll = wave.wave.authenticated_user_eligible_for_admin === true;
  let t6;
  if ($[12] !== fixedDropMode || $[13] !== setToast || $[14] !== wave.chat.authenticated_user_eligible || $[15] !== wave.participation.authenticated_user_eligible) {
    t6 = newIsDropMode => {
      if (fixedDropMode !== DropMode.BOTH) {
        return false;
      }
      if (newIsDropMode && !wave.participation.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to drop in this wave",
          type: "error"
        });
        return false;
      }
      if (!newIsDropMode && !wave.chat.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to chat in this wave",
          type: "error"
        });
        return false;
      }
      return true;
    };
    $[12] = fixedDropMode;
    $[13] = setToast;
    $[14] = wave.chat.authenticated_user_eligible;
    $[15] = wave.participation.authenticated_user_eligible;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const canSwitchDropMode = t6;
  let t7;
  if ($[17] !== canSwitchDropMode || $[18] !== fixedDropMode || $[19] !== modeScopeToken || $[20] !== onExitFixedDropMode) {
    t7 = newIsDropMode_0 => {
      if (!newIsDropMode_0 && fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode) {
        setCurationPrefillSeed(null);
        setDismissedQuorumProposalScope(null);
        onExitFixedDropMode();
        return;
      }
      if (!canSwitchDropMode(newIsDropMode_0)) {
        return;
      }
      setCurationPrefillSeed(null);
      setDismissedQuorumProposalScope(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: newIsDropMode_0
      });
    };
    $[17] = canSwitchDropMode;
    $[18] = fixedDropMode;
    $[19] = modeScopeToken;
    $[20] = onExitFixedDropMode;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const onDropModeChange = t7;
  let t8;
  if ($[22] !== canSwitchDropMode || $[23] !== modeScopeToken) {
    t8 = url => {
      if (!canSwitchDropMode(true)) {
        return;
      }
      setCurationPrefillSeed({
        scopeKey: modeScopeToken,
        url
      });
      setDismissedQuorumProposalScope(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: true
      });
    };
    $[22] = canSwitchDropMode;
    $[23] = modeScopeToken;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const onSwitchToDropModeWithUrl = t8;
  let t9;
  if ($[25] !== fixedDropMode || $[26] !== onDropModeChange || $[27] !== onExitFixedDropMode || $[28] !== quorumProposalScopeKey || $[29] !== wave.chat.authenticated_user_eligible) {
    t9 = () => {
      if (fixedDropMode === DropMode.BOTH && wave.chat.authenticated_user_eligible) {
        onDropModeChange(false);
        return;
      }
      if (fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode !== undefined) {
        onDropModeChange(false);
        return;
      }
      setDismissedQuorumProposalScope(quorumProposalScopeKey);
    };
    $[25] = fixedDropMode;
    $[26] = onDropModeChange;
    $[27] = onExitFixedDropMode;
    $[28] = quorumProposalScopeKey;
    $[29] = wave.chat.authenticated_user_eligible;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const onCloseQuorumProposal = t9;
  let t10;
  if ($[31] !== isDropMode || $[32] !== onDropModeChange) {
    t10 = () => {
      setDismissedQuorumProposalScope(null);
      if (!isDropMode) {
        onDropModeChange(true);
      }
    };
    $[31] = isDropMode;
    $[32] = onDropModeChange;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const onOpenQuorumProposal = t10;
  let t11;
  if ($[34] !== canMentionAll) {
    t11 = partIndex => {
      setDrop(prevDrop => {
        if (!prevDrop) {
          return null;
        }
        const newParts = prevDrop.parts.filter((_, i) => i !== partIndex);
        return {
          ...prevDrop,
          parts: newParts,
          referenced_nfts: prevDrop.referenced_nfts,
          mentioned_users: prevDrop.mentioned_users,
          mentioned_groups: getMentionedGroupsFromParts(newParts, canMentionAll),
          mentioned_waves: prevDrop.mentioned_waves ?? [],
          metadata: prevDrop.metadata
        };
      });
    };
    $[34] = canMentionAll;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const onRemovePart = t11;
  let t12;
  if ($[36] !== curationComposerVariant || $[37] !== processDropRemoved || $[38] !== processIncomingDrop || $[39] !== setToast || $[40] !== submissionExperience) {
    t12 = (serverDrop, body_0) => {
      if (body_0.dropId) {
        processDropRemoved(body_0.drop.wave_id, body_0.dropId);
      }
      processIncomingDrop(serverDrop, ProcessIncomingDropType.DROP_INSERT);
      body_0.onSuccess?.();
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && curationComposerVariant === "leaderboard") {
        setToast({
          message: "Drop submitted successfully",
          type: "success"
        });
      }
    };
    $[36] = curationComposerVariant;
    $[37] = processDropRemoved;
    $[38] = processIncomingDrop;
    $[39] = setToast;
    $[40] = submissionExperience;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== processDropRemoved || $[43] !== setToast) {
    t13 = (error, body_1) => {
      setTimeout(() => {
        if (body_1.dropId) {
          processDropRemoved(body_1.drop.wave_id, body_1.dropId);
        }
      }, 0);
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
      body_1.onError?.(error);
    };
    $[42] = processDropRemoved;
    $[43] = setToast;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t12 || $[46] !== t13) {
    t14 = {
      mutationFn: _temp,
      onSuccess: t12,
      onError: t13,
      retry: _temp2,
      retryDelay: _temp3
    };
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const addDropMutation = useMutation(t14);
  let t15;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [];
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const queueRef = useRef(t15);
  const isProcessingRef = useRef(false);
  const hasBatchErrorsRef = useRef(false);
  const inFlightProcessNextDropRef = useRef(null);
  let t16;
  if ($[49] !== addDropMutation || $[50] !== onAllDropsAdded || $[51] !== waitAndInvalidateDrops) {
    t16 = async () => {
      if (isProcessingRef.current || queueRef.current.length === 0) {
        return;
      }
      isProcessingRef.current = true;
      while (queueRef.current.length > 0) {
        const dropRequest = queueRef.current.shift();
        if (dropRequest === undefined) {
          break;
        }
        ;
        try {
          await addDropMutation.mutateAsync(dropRequest);
        } catch (t17) {
          const error_0 = t17;
          hasBatchErrorsRef.current = true;
          console.error("Error processing drop:", error_0);
        }
      }
      isProcessingRef.current = false;
      const shouldNotifyAllDropsAdded = !hasBatchErrorsRef.current;
      hasBatchErrorsRef.current = false;
      waitAndInvalidateDrops();
      if (shouldNotifyAllDropsAdded) {
        onAllDropsAdded?.();
      }
    };
    $[49] = addDropMutation;
    $[50] = onAllDropsAdded;
    $[51] = waitAndInvalidateDrops;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  const processNextDrop = t16;
  let t17;
  if ($[53] !== onDropAddedToQueue || $[54] !== processNextDrop || $[55] !== unreadDividerContext) {
    t17 = dropRequest_0 => {
      queueRef.current.push(dropRequest_0);
      inFlightProcessNextDropRef.current = processNextDrop();
      if (unreadDividerContext) {
        unreadDividerContext.setUnreadDividerSerialNo(null);
      }
      onDropAddedToQueue();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    $[53] = onDropAddedToQueue;
    $[54] = processNextDrop;
    $[55] = unreadDividerContext;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  const submitDrop = t17;
  const hasExitFixedDropMode = onExitFixedDropMode !== undefined;
  const t18 = fixedDropMode === DropMode.BOTH || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  const t19 = fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode ? "Close create drop" : null;
  const t20 = fixedDropMode === DropMode.BOTH && privileges.chatRestriction === null || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  let t21;
  if ($[57] !== activeDrop || $[58] !== drop || $[59] !== dropId || $[60] !== externalAttachmentDrop || $[61] !== isDropMode || $[62] !== isStormMode || $[63] !== onCancelReplyQuote || $[64] !== onDropModeChange || $[65] !== onExternalAttachmentDropConsumed || $[66] !== onSwitchToDropModeWithUrl || $[67] !== privileges || $[68] !== submitDrop || $[69] !== t18 || $[70] !== t19 || $[71] !== t20) {
    t21 = {
      activeDrop,
      onCancelReplyQuote,
      drop,
      isStormMode,
      isDropMode,
      dropId,
      setDrop,
      setIsStormMode,
      onDropModeChange,
      onSwitchToDropModeWithUrl,
      submitDrop,
      privileges,
      showDropModeToggle: t18,
      dropModeToggleExitLabel: t19,
      canExitDropMode: t20,
      externalAttachmentDrop,
      onExternalAttachmentDropConsumed
    };
    $[57] = activeDrop;
    $[58] = drop;
    $[59] = dropId;
    $[60] = externalAttachmentDrop;
    $[61] = isDropMode;
    $[62] = isStormMode;
    $[63] = onCancelReplyQuote;
    $[64] = onDropModeChange;
    $[65] = onExternalAttachmentDropConsumed;
    $[66] = onSwitchToDropModeWithUrl;
    $[67] = privileges;
    $[68] = submitDrop;
    $[69] = t18;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  const createDropContentProps = t21;
  let dropComposerContent;
  if (isQuorumProposalDropMode) {
    let t22;
    if ($[73] !== activeDrop || $[74] !== dropId || $[75] !== isQuorumProposalModalOpen || $[76] !== onCancelReplyQuote || $[77] !== onCloseQuorumProposal || $[78] !== submitDrop || $[79] !== wave) {
      t22 = <QuorumProposalDropModal isOpen={isQuorumProposalModalOpen} activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} submitDrop={submitDrop} onClose={onCloseQuorumProposal} />;
      $[73] = activeDrop;
      $[74] = dropId;
      $[75] = isQuorumProposalModalOpen;
      $[76] = onCancelReplyQuote;
      $[77] = onCloseQuorumProposal;
      $[78] = submitDrop;
      $[79] = wave;
      $[80] = t22;
    } else {
      t22 = $[80];
    }
    let t23;
    if ($[81] !== isQuorumProposalModalOpen || $[82] !== onOpenQuorumProposal) {
      t23 = !isQuorumProposalModalOpen && <div className="tw-flex tw-w-full tw-justify-end"><button type="button" onClick={onOpenQuorumProposal} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-100 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800">Create Proposal</button></div>;
      $[81] = isQuorumProposalModalOpen;
      $[82] = onOpenQuorumProposal;
      $[83] = t23;
    } else {
      t23 = $[83];
    }
    let t24;
    if ($[84] !== t22 || $[85] !== t23) {
      t24 = <>{t22}{t23}</>;
      $[84] = t22;
      $[85] = t23;
      $[86] = t24;
    } else {
      t24 = $[86];
    }
    dropComposerContent = t24;
  } else {
    if (isCurationDropMode) {
      let t22;
      if ($[87] !== activeDrop || $[88] !== curationComposerVariant || $[89] !== dropId || $[90] !== initialCurationUrl || $[91] !== isDropMode || $[92] !== onCancelReplyQuote || $[93] !== submitDrop || $[94] !== wave) {
        t22 = <CreateCurationDropContent activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} isDropMode={isDropMode} initialUrl={initialCurationUrl} submitDrop={submitDrop} curationComposerVariant={curationComposerVariant} />;
        $[87] = activeDrop;
        $[88] = curationComposerVariant;
        $[89] = dropId;
        $[90] = initialCurationUrl;
        $[91] = isDropMode;
        $[92] = onCancelReplyQuote;
        $[93] = submitDrop;
        $[94] = wave;
        $[95] = t22;
      } else {
        t22 = $[95];
      }
      dropComposerContent = t22;
    } else {
      let t22;
      if ($[96] !== createDropContentProps || $[97] !== submissionExperience || $[98] !== wave) {
        t22 = <CreateDropContent {...createDropContentProps} wave={wave} submissionExperience={submissionExperience} />;
        $[96] = createDropContentProps;
        $[97] = submissionExperience;
        $[98] = wave;
        $[99] = t22;
      } else {
        t22 = $[99];
      }
      dropComposerContent = t22;
    }
  }
  let t22;
  if ($[100] !== drop?.mentioned_groups || $[101] !== drop?.mentioned_users || $[102] !== drop?.mentioned_waves || $[103] !== drop?.parts || $[104] !== drop?.referenced_nfts || $[105] !== isCurationDropMode || $[106] !== isQuorumProposalDropMode || $[107] !== isStormMode || $[108] !== onRemovePart) {
    t22 = isStormMode && !isCurationDropMode && !isQuorumProposalDropMode && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: ANIMATION_DURATION
    }}><CreateDropStormParts parts={drop?.parts ?? []} mentionedUsers={drop?.mentioned_users ?? []} mentionedGroups={drop?.mentioned_groups ?? []} mentionedWaves={drop?.mentioned_waves ?? []} referencedNfts={drop?.referenced_nfts ?? []} onRemovePart={onRemovePart} /></motion.div>;
    $[100] = drop?.mentioned_groups;
    $[101] = drop?.mentioned_users;
    $[102] = drop?.mentioned_waves;
    $[103] = drop?.parts;
    $[104] = drop?.referenced_nfts;
    $[105] = isCurationDropMode;
    $[106] = isQuorumProposalDropMode;
    $[107] = isStormMode;
    $[108] = onRemovePart;
    $[109] = t22;
  } else {
    t22 = $[109];
  }
  let t23;
  if ($[110] !== t22) {
    t23 = <AnimatePresence>{t22}</AnimatePresence>;
    $[110] = t22;
    $[111] = t23;
  } else {
    t23 = $[111];
  }
  let t24;
  if ($[112] !== dropComposerContent || $[113] !== t23) {
    t24 = <>{t23}{dropComposerContent}</>;
    $[112] = dropComposerContent;
    $[113] = t23;
    $[114] = t24;
  } else {
    t24 = $[114];
  }
  return t24;
}
function _temp3(failureCount_0) {
  return failureCount_0 * 1000;
}
function _temp2(failureCount) {
  return failureCount < 3;
}
async function _temp(body) {
  return commonApiPost({
    endpoint: "drops",
    body: body.drop
  });
}